import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { FaFileExcel } from 'react-icons/fa';
import { monitoringApi, Classification, ListParams } from '../../../services/monitoringApi';

interface ExportDropdownProps {
    filters: ListParams;
}

const formatTaxIdExport = (taxId: string | undefined): string => {
    if (!taxId) return '';
    const clean = taxId.replace(/[^\dA-Za-z]/g, '');
    if (clean.length === 11 && /^\d+$/.test(clean)) {
        return `${clean.slice(0, 3)}.${clean.slice(3, 6)}.${clean.slice(6, 9)}-${clean.slice(9)}`;
    }
    if (clean.length === 14) {
        return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5, 8)}/${clean.slice(8, 12)}-${clean.slice(12)}`;
    }
    return clean;
};

/**
 * Parseia uma string de data para Date local, tratando YYYY-MM-DD como data local
 * (não UTC) para evitar deslocamento de timezone.
 * AF-3447: new Date("YYYY-MM-DD") é interpretado como UTC pela spec JS,
 * causando dia anterior em BRT. Essa função normaliza para data local.
 */
const parseLocalDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    // Se é date-only (YYYY-MM-DD), parsear como local
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [y, m, d] = dateStr.split('-').map(Number);
        return new Date(y, m - 1, d);
    }
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateBR = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const d = parseLocalDate(dateStr);
    if (!d) return '';
    return d.toLocaleDateString('pt-BR');
};

const calcNextMonitoring = (lastAnalysisAt: string | null, riskLevel: string): string => {
    if (!lastAnalysisAt) return '';
    const d = parseLocalDate(lastAnalysisAt);
    if (!d) return '';
    // AF-3447: Calcular a partir da data de calendário local (sem hora),
    // consistente com o backend que agora usa data BRT.
    const localDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const days = (riskLevel === 'HIGH' || riskLevel === 'EXTREME') ? 25 : 35;
    localDate.setDate(localDate.getDate() + days);
    return localDate.toLocaleDateString('pt-BR');
};

export default function ExportDropdown({ filters }: ExportDropdownProps) {
    const [isExporting, setIsExporting] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const fetchAllData = async (): Promise<Classification[]> => {
        // Fetch all pages to export complete dataset
        const allItems: Classification[] = [];
        let page = 1;
        const pageSize = 100;
        let hasMore = true;

        while (hasMore) {
            const result = await monitoringApi.list({ ...filters, page, pageSize });
            const items = result.data?.items || [];
            allItems.push(...items);
            hasMore = items.length === pageSize;
            page++;
        }

        return allItems;
    };

    const generateCSV = (items: Classification[]): string => {
        const headers = [
            'CPF/CNPJ',
            'Nome/Razão Social',
            'Nível de Risco',
            'Último Monitoramento',
            'Próximo Monitoramento',
            'Data de Cadastro',
            'Status',
            'Divergência',
        ];

        const rows = items.map(item => [
            formatTaxIdExport(item.tax_id),
            item.person_name || '',
            item.risk_level || '',
            formatDateBR(item.last_analysis_at),
            calcNextMonitoring(item.last_analysis_at, item.risk_level),
            formatDateBR(item.created_at),
            item.active ? 'Ativo' : 'Inativo',
            item.has_pending_divergence ? 'Sim' : 'Não',
        ]);

        return [headers, ...rows]
            .map(row => row.map(field => {
                let val = String(field).replace(/"/g, '""');
                // Neutralize formula injection for Excel (prefix with single quote)
                if (/^[=+\-@\t\r]/.test(val)) {
                    val = `'${val}`;
                }
                return `"${val}"`;
            }).join(';'))
            .join('\n');
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const items = await fetchAllData();

            if (items.length === 0) {
                toast.warning('Nenhum registro para exportar');
                return;
            }

            const csvContent = generateCSV(items);
            const timestamp = new Date().toISOString().split('T')[0];
            const filename = `monitoramento-pld-${timestamp}.csv`;

            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast.success(`${items.length} registros exportados com sucesso`);
        } catch {
            toast.error('Não foi possível concluir a exportação. Tente novamente.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div ref={dropdownRef}>
            <button
                type="button"
                onClick={handleExport}
                disabled={isExporting}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                aria-label="Exportar dados em CSV"
            >
                {isExporting ? (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                ) : (
                    <FaFileExcel size={14} />
                )}
                {isExporting ? 'Exportando...' : 'Exportar CSV'}
            </button>
        </div>
    );
}
